import { DateOnly } from '@if/core/date'
import { EProductsInsurance, EProvince } from '@if/core/types'
import { normalizeNumber } from '@if/core/utils'
import { ActionMenuItem, ControlledTextField, FieldsetCard, SubsectionTitle } from '@if/mui'

import { Box, Divider, Stack } from '@mui/material'
import { ProductsProvider } from '@src/types/ProductsProvider'
import { ProductsWorksheet } from '@src/types/WorksheetSchema'
import { JSX, useEffect, useMemo } from 'react'
import { Control, FieldErrors, UseFormGetValues, UseFormSetValue, UseFormTrigger, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import ProductsInsuranceDetails from './ProductsInsuranceDetails'

interface Props {
  insurancesProvider: ProductsProvider[]
  warrantyProvider: ProductsProvider[]
  control: Control<ProductsWorksheet>
  setValue: UseFormSetValue<ProductsWorksheet>
  errors: FieldErrors<ProductsWorksheet>
  getValues: UseFormGetValues<ProductsWorksheet>
  trigger: UseFormTrigger<ProductsWorksheet>
  stateIso: EProvince
  hasContractEndDate: boolean
  isSymTechPrime: boolean
}

const ProductsInsurances = ({
  insurancesProvider,
  warrantyProvider,
  control,
  setValue,
  stateIso,
  hasContractEndDate,
  isSymTechPrime,
}: Props) => {
  const { t } = useTranslation()
  const today = useMemo(() => {
    const now = new Date()
    return DateOnly.fromComponents(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  }, [])

  const replacementOrGapInsurance = useWatch({
    control,
    name: 'replacementOrGapInsurance',
  })

  const creditInsurance = useWatch({
    control,
    name: 'creditInsurance',
  })

  const extendedWarranty = useWatch({
    control,
    name: 'extendedWarranty',
  })

  useEffect(() => {
    const getPremium = (value: number | null | undefined) => {
      const amount = Number(value)
      return Number.isNaN(amount) ? 0 : amount
    }

    const amount = normalizeNumber(
      getPremium(replacementOrGapInsurance?.amount) +
        getPremium(creditInsurance?.amount) +
        getPremium(extendedWarranty?.amount),
    )

    setValue('amountRequested', amount)
  }, [setValue, replacementOrGapInsurance?.amount, creditInsurance?.amount, extendedWarranty?.amount])

  const insuranceProviders = useMemo<JSX.Element[]>(() => {
    return insurancesProvider.map((provider) => (
      <ActionMenuItem key={provider.id} value={provider.id}>
        {provider.name}
      </ActionMenuItem>
    ))
  }, [insurancesProvider])

  const warrantyProviders = useMemo<JSX.Element[]>(() => {
    return warrantyProvider.map((provider) => (
      <ActionMenuItem key={provider.id} value={provider.id}>
        {provider.name}
      </ActionMenuItem>
    ))
  }, [warrantyProvider])

  return (
    <Stack direction={{ lg: 'row', md: 'column' }} spacing={2} justifyContent="space-between" alignItems="flex-start">
      <Box sx={{ flex: { lg: 2 }, minWidth: 0, width: '100%', maxWidth: isSymTechPrime ? { lg: '50%' } : 'none' }}>
        <FieldsetCard title={t('productsWorksheet.insurances')}>
          <Stack direction="row" divider={<Divider orientation="vertical" flexItem />} spacing={2}>
            {/* Replacement / GAP Insurance */}
            {!isSymTechPrime && (
              <Box flex={1} minWidth={0}>
                <ControlledTextField
                  select
                  control={control}
                  name="replacementOrGapInsurance.provider"
                  label={t('common.provider')}
                  fullWidth
                >
                  <ActionMenuItem value="" />
                  {insuranceProviders}
                </ControlledTextField>
                <SubsectionTitle sx={{ mb: 2, mt: 2 }}>
                  {stateIso === EProvince.Quebec
                    ? t('productsWorksheet.replacementInsurance')
                    : t('productsWorksheet.gapInsurance')}
                </SubsectionTitle>
                <ProductsInsuranceDetails
                  control={control}
                  insurance={EProductsInsurance.ReplacementOrGapInsurance}
                  today={today}
                  setValue={setValue}
                  hasContractEndDate={hasContractEndDate}
                />
              </Box>
            )}

            {/* Credit Insurance */}
            <Box flex={1} minWidth={0}>
              <ControlledTextField
                select
                control={control}
                name="creditInsurance.provider"
                label={t('common.provider')}
                fullWidth
                disabled={isSymTechPrime}
              >
                <ActionMenuItem value="" />
                {insuranceProviders}
              </ControlledTextField>
              <SubsectionTitle sx={{ mb: 2, mt: 2 }}>{t('productsWorksheet.creditInsurance')}</SubsectionTitle>
              <ProductsInsuranceDetails
                control={control}
                insurance={EProductsInsurance.CreditInsurance}
                today={today}
                setValue={setValue}
                hasContractEndDate={hasContractEndDate}
              />
            </Box>
          </Stack>
        </FieldsetCard>
      </Box>
      {!isSymTechPrime && (
        <Box sx={{ flex: { lg: 1 }, minWidth: 0, width: '100%' }}>
          <FieldsetCard title={t('productsWorksheet.extendedWarranty')}>
            <ControlledTextField
              select
              control={control}
              name="extendedWarranty.provider"
              label={t('common.provider')}
              fullWidth
            >
              <ActionMenuItem value="" />
              {warrantyProviders}
            </ControlledTextField>
            <Box>
              <SubsectionTitle sx={{ mb: 2, mt: 2 }}>{t('productsWorksheet.extendedWarranty')}</SubsectionTitle>
              <ProductsInsuranceDetails
                control={control}
                insurance={EProductsInsurance.ExtendedWarranty}
                today={today}
                setValue={setValue}
                hasContractEndDate={hasContractEndDate}
              />
            </Box>
          </FieldsetCard>
        </Box>
      )}
    </Stack>
  )
}

export default ProductsInsurances
